/**
 * Chris Packham — permanent feature (baked from the winning Varify A/B tests).
 *
 * Two surfaces, two different audiences:
 *   1. CART QUOTE — EVERYONE, site-wide. The CP quote below the Checkout button in
 *      the Recharge (RSE) slide-out cart, wherever the shopper opens it, whatever
 *      brought them to the site. (Won on Packham ad traffic in Varify test A, then
 *      won again — decisively — on the complement segment in test C, so it is now
 *      simply the default experience. No cart-contents gate: shows on every cart.)
 *   2. PDP IMAGE — Packham ad traffic ONLY. On the bamboo toilet-paper PDP, inject
 *      the CP image as the FIRST carousel slide (shown on load) + move the MBG
 *      guarantee sticker to the bottom-right. Gated because it only paid off for
 *      shoppers who had already seen Packham in the ad creative.
 *
 * The ad param (any `?…=…packham…`, e.g. content=packham2026) only exists on the
 * landing URL, so it's captured into a first-party `cp_ref` cookie the first time we
 * see it and the PDP image is gated on that cookie, so the whole session persists.
 * Loaded site-wide from layout/theme.liquid.
 *
 * Replaces Varify tests A + B (retired 2026-07-30) and test C (retire it once this
 * is live, or the two will fight over the same injection point).
 */
(function () {
  'use strict';

  // ── CONFIG ────────────────────────────────────────────────────
  const HANDLE       = 'unbleached-bamboo-toilet-paper';
  const IMAGE_URL    = 'https://cdn.shopify.com/s/files/1/0279/9373/5243/files/CP_Bamboo_TP_Carousel_Image.webp?v=1786046500';
  const IMAGE_ALT    = 'Chris Packham — no bleach, no harsh chemicals';
  const HEADSHOT_URL = 'https://cdn.shopify.com/s/files/1/0279/9373/5243/files/chris_packham_headshot.webp?v=1784138061';
  const QUOTE        = 'Costs less per sheet than big supermarket brands, and it costs the planet a lot less too.';
  const ATTRIBUTION  = 'Chris Packham';
  const INSERT_AT    = 0;                 // CP is the first slide (shown on load)
  const FAKE_MEDIA_ID = 9000000000001;    // synthetic id — can't collide with real Shopify media ids
  const SLIDE_MARK   = 'data-ab-cp-slide';
  const QUOTE_MARK   = 'data-ab-cp-quote';
  const HOST_ID      = 'recharge-storefront-experiences__cart-drawer';
  // ──────────────────────────────────────────────────────────────

  // ── Packham ad gate (PDP image only — the cart quote is for everyone) ─────────
  // The ad's ?content=…packham… param only exists on the landing URL, so capture it
  // into a cookie the first time we see it and read the cookie thereafter, so the whole
  // session (landing → PDP, across page loads) stays in.
  function isPackhamTraffic() {
    if (/packham/i.test(location.search)) {
      document.cookie = 'cp_ref=1; path=/; max-age=' + (60 * 60 * 24 * 30) + '; SameSite=Lax';
      return true; // don't re-read: the cookie isn't readable back if cookies are blocked
    }
    return /(?:^|;)\s*cp_ref=/.test(document.cookie);
  }

  // ════════════════════════════════════════════════════════════════
  // MODULE 1 — CP image as slide 0 on the bamboo TP PDP (Packham ad traffic only)
  // ════════════════════════════════════════════════════════════════
  function initPdpImage() {
    function findMainSlideshow() {
      const shows = window.slideshows;
      if (!shows) return null;
      for (const key in shows) {
        const sp = shows[key];
        const root = sp && sp.root;
        if (!root || !root.classList || !root.classList.contains('splide--product')) continue;
        if (typeof sp.add === 'function' &&
            (root.classList.contains('is-initialized') || root.querySelector('.splide__list > li'))) {
          return { el: root, sp: sp, id: root.id || key };
        }
      }
      return null;
    }

    // Native member of the Alpine gallery: x-show drives desktop visibility (Splide is
    // destroyed >=990px); the :class strips lg:hidden so x-show wins after hydration.
    function buildSlide() {
      const t = document.createElement('template');
      t.innerHTML =
        '<li class="feature-media-item block max-lg:[.splide:not(.is-active)_&]:hidden lg:hidden w-full splide__slide px-section lg:w-full lg:px-0" ' +
        SLIDE_MARK + ' data-media-id="' + FAKE_MEDIA_ID + '" ' +
        'x-transition.opacity.duration.200ms ' +
        'x-show="isMaxLgBreakpoint || currentMediaId === ' + FAKE_MEDIA_ID + '" ' +
        ':class="{ \'lg:hidden\' : false }">' +
          '<div data-product-single-media-wrapper="' + FAKE_MEDIA_ID + '"><img src="' + IMAGE_URL + '" alt="' + IMAGE_ALT + '" loading="lazy" ' +
            'class="media__styled-element" style="display:block;width:100%;height:auto;"></div>' +
        '</li>';
      return t.content.firstChild;
    }

    // Plain HTML — NO Alpine directives (the thumbnail lists resolve to a scope without
    // currentMediaId, so bindings would throw). Click + active state wired in JS below.
    function buildThumb() {
      const t = document.createElement('template');
      t.innerHTML =
        '<li class="product-thumbnail-list-item w-20 flex-shrink-0 scroll-snap-align-start ' +
        'lg:w-auto lg:flex-shrink lg:scroll-snap-align-none relative pb-media-shadow-half pr-media-shadow-half" ' +
        SLIDE_MARK + '>' +
          '<a role="button" tabindex="0" class="media-thumbnail block" href="' + IMAGE_URL + '" style="cursor:pointer;">' +
            '<div class="media--small relative"><img src="' + IMAGE_URL + '" alt="' + IMAGE_ALT + '" loading="lazy" ' +
              'class="media__styled-element" style="display:block;width:100%;aspect-ratio:1/1;object-fit:cover;"></div>' +
          '</a>' +
        '</li>';
      return t.content.firstChild;
    }

    function inject(ctx) {
      const el = ctx.el, sp = ctx.sp;
      const list = el.querySelector('.feature-media-list') || el.querySelector('.splide__list');
      if (!list) { console.warn('[CP] media list not found'); return; }
      if (list.querySelector('[' + SLIDE_MARK + ']')) return; // idempotent

      // Splide is destroyed on desktop (990 breakpoint) and live on mobile.
      const S = window.Splide;
      const destroyed = !!(S && sp.state && sp.state.is(S.STATES.DESTROYED));
      const slide = buildSlide();
      if (destroyed) { list.insertBefore(slide, list.children[INSERT_AT] || null); }
      else { sp.add(slide, INSERT_AT); }
      if (window.Alpine && window.Alpine.initTree) window.Alpine.initTree(slide);

      // Drive the Product component directly (scope-independent) so the injected
      // thumbnail navigates the gallery on both breakpoints.
      const showCP = function () {
        try {
          const d = window.Alpine && window.Alpine.$data && window.Alpine.$data(el);
          if (d) d.currentMediaId = FAKE_MEDIA_ID;
        } catch (e) {}
      };

      // Two thumbnail lists (desktop grid + mobile strip). Inject into every one.
      const thumbLists = new Set();
      document.querySelectorAll('.product-thumbnail-list-item').forEach(function (li) {
        const ul = li.closest('ul');
        if (ul) thumbLists.add(ul);
      });
      const injectedThumbs = [];
      thumbLists.forEach(function (ul) {
        if (ul.querySelector('[' + SLIDE_MARK + ']')) return;
        const thumb = buildThumb();
        ul.insertBefore(thumb, ul.children[INSERT_AT] || null);
        const link = thumb.querySelector('.media-thumbnail');
        const activate = function (e) { if (e) e.preventDefault(); showCP(); };
        link.addEventListener('click', activate);
        link.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') activate(e);
        });
        injectedThumbs.push(thumb);
      });

      // Active-state highlight, event-driven (replaces the removed Alpine :class).
      if (injectedThumbs.length) {
        document.addEventListener('shapes:product:mediachange', function (e) {
          const on = !!(e.detail && e.detail.mediaId === FAKE_MEDIA_ID);
          injectedThumbs.forEach(function (li) {
            li.classList.toggle('product-thumbnail-list-item--active', on);
          });
        });
      }

      // Make CP the media shown on load. The theme sets currentMediaId to the featured
      // image during hydration and that can land after ours — re-assert across a short
      // window, but stop the moment the shopper interacts.
      let userTouched = false;
      document.addEventListener('pointerdown', function () { userTouched = true; }, { capture: true, once: true });
      const forceCP = function () {
        if (userTouched) return;
        try {
          const d = window.Alpine && window.Alpine.$data && window.Alpine.$data(el);
          if (d && d.currentMediaId !== FAKE_MEDIA_ID) d.currentMediaId = FAKE_MEDIA_ID;
        } catch (e) {}
      };
      forceCP();
      if (window.requestAnimationFrame) requestAnimationFrame(forceCP);
      [80, 200, 400, 800, 1400].forEach(function (ms) { setTimeout(forceCP, ms); });

      // Move the MBG guarantee sticker(s) to the bottom-right of the image (desktop
      // inset 14px; mobile 84px up, past where .splide--product runs toward the thumbs).
      (function repositionStickers() {
        if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
        const mbg = [].slice.call(document.querySelectorAll('.sticker')).filter(function (s) {
          return s.querySelector('img[src*="MBGbrown"], img[srcset*="MBGbrown"]');
        });
        mbg.forEach(function (s) {
          if (!el.contains(s)) el.appendChild(s);
          s.setAttribute('data-cp-sticker', '');
          s.style.top = s.style.left = s.style.right = s.style.bottom = '';
          const tc = s.querySelector('.sticker__translate-container');
          if (tc) tc.style.transform = '';
        });
        const STYLE_ID = 'cp-sticker-reposition-style';
        if (mbg.length && !document.getElementById(STYLE_ID)) {
          const st = document.createElement('style');
          st.id = STYLE_ID;
          st.textContent =
            '.sticker[data-cp-sticker]{top:auto!important;left:auto!important;right:14px!important;bottom:14px!important;}' +
            '.sticker[data-cp-sticker] .sticker__translate-container{transform:none!important;}' +
            '@media (max-width:989px){.sticker[data-cp-sticker]{right:12px!important;bottom:84px!important;}}';
          document.head.appendChild(st);
        }
      })();

      console.log('[CP] PDP image + thumbnail integrated');
    }

    const found = findMainSlideshow();
    if (found) { inject(found); return; }
    let tries = 0;
    const timer = setInterval(function () {
      const s = findMainSlideshow();
      if (s) { clearInterval(timer); inject(s); }
      else if (++tries > 100) { clearInterval(timer); console.warn('[CP] product slideshow not found after ~10s'); }
    }, 100);
  }

  // ════════════════════════════════════════════════════════════════
  // MODULE 2 — CP quote in the RSE slide-out cart (site-wide, ALL traffic, ungated)
  // ════════════════════════════════════════════════════════════════
  function initCartQuote() {
    function buildQuote() {
      const wrap = document.createElement('div');
      wrap.setAttribute(QUOTE_MARK, '');
      // Fully inline-styled: the cart is an OPEN shadow root, theme CSS doesn't reach in.
      wrap.style.cssText = 'display:flex;align-items:center;gap:12px;padding:14px 20px 18px;';
      wrap.innerHTML =
        '<img src="' + HEADSHOT_URL + '" alt="' + ATTRIBUTION + '" ' +
             'style="width:56px;height:56px;border-radius:9999px;object-fit:cover;flex:0 0 auto;" />' +
        '<div style="font-size:14px;line-height:1.4;">' +
          '<p style="margin:0 0 4px;font-style:italic;">' + '“' + QUOTE + '”' + '</p>' +
          '<p style="margin:0;font-weight:700;">' + ATTRIBUTION + '</p>' +
        '</div>';
      return wrap;
    }

    function inject(shadow) {
      if (!shadow || shadow.querySelector('[' + QUOTE_MARK + ']')) return; // idempotent
      const btn = shadow.querySelector('[name="checkout"]');
      if (!btn) return;
      const form = btn.closest('form');
      const anchor = (form && form.parentElement) || btn;
      anchor.insertAdjacentElement('afterend', buildQuote());
      console.log('[CP] cart quote injected');
    }

    function watch(host) {
      const shadow = host.shadowRoot;
      if (!shadow) return;
      inject(shadow);
      // RSE re-renders wipe injected DOM — re-inject on any shadow mutation.
      new MutationObserver(function () { inject(shadow); })
        .observe(shadow, { childList: true, subtree: true });
    }

    const existing = document.getElementById(HOST_ID);
    if (existing && existing.shadowRoot) { watch(existing); return; }
    const bodyObs = new MutationObserver(function () {
      const host = document.getElementById(HOST_ID);
      if (host && host.shadowRoot) { bodyObs.disconnect(); watch(host); }
    });
    bodyObs.observe(document.body, { childList: true, subtree: true });
  }

  // ── boot ──────────────────────────────────────────────────────
  // Capture the ad param site-wide FIRST — ads may land somewhere other than the PDP,
  // and the param is only ever on that landing URL.
  const isPackham = isPackhamTraffic();

  initCartQuote();                                                    // everyone
  if (isPackham && location.pathname.includes('/products/' + HANDLE)) // ad traffic only
    initPdpImage();
})();
